/**
 * Media policy for catalog cards (wizard Step 2 and similar grids).
 *
 * Catalog cards must never start heavy media on their own. Pre-recorded preview loops
 * (see templatePreviews.ts) may autoplay only when the visitor has not asked for reduced
 * motion and is not on a data-saver / very slow connection; otherwise cards show the poster
 * and a tap-to-play control.
 */

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

function matches(query: string): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export function prefersReducedMotion(): boolean {
  return matches('(prefers-reduced-motion: reduce)');
}

export function isConstrainedConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
}

/** True when preview loops may autoplay (muted) once in view; false → poster + tap-to-play. */
export function shouldAutoplayCardLoops(): boolean {
  return !prefersReducedMotion() && !isConstrainedConnection();
}

export const MOBILE_LOOP_CAP = 2;
export const DESKTOP_LOOP_CAP = 3;

/** Max number of preview loops allowed to play at the same time. */
export function loopConcurrencyCap(): number {
  return matches('(max-width: 767px)') ? MOBILE_LOOP_CAP : DESKTOP_LOOP_CAP;
}
